"use client"

import { createContext, ReactNode, useContext } from "react"

export type BatchDataEntry =
  | {
      data?: unknown[]
      meta?: unknown
      error?: string
    }
  | undefined

export type BatchDataMap = Record<string, BatchDataEntry>

type ResolveBatchDataEntryOptions = {
  key: string
  prefix?: string
  suffix?: string
}

const BatchDataContext = createContext<BatchDataMap>({})

type BatchDataProviderProps = {
  data?: BatchDataMap
  children: ReactNode
}

export const BatchDataProvider = ({ data, children }: BatchDataProviderProps) => {
  return <BatchDataContext.Provider value={data || {}}>{children}</BatchDataContext.Provider>
}

export const useBatchDataMap = () => useContext(BatchDataContext)

export const resolveBatchDataEntry = <T = BatchDataEntry,>(
  data: BatchDataMap,
  { key, prefix, suffix }: ResolveBatchDataEntryOptions,
): T | undefined => {
  const exactMatch = data[key]
  if (exactMatch) {
    return exactMatch as T
  }

  if (!prefix && !suffix) {
    return undefined
  }

  const matchedEntries = Object.entries(data).filter(([entryKey]) => {
    if (prefix && !entryKey.startsWith(prefix)) {
      return false
    }

    if (suffix && !entryKey.endsWith(suffix)) {
      return false
    }

    return true
  })

  if (matchedEntries.length === 1) {
    return matchedEntries[0][1] as T
  }

  return undefined
}

export const useBatchData = <T = BatchDataEntry,>(key: string): T | undefined => {
  const data = useBatchDataMap()
  return resolveBatchDataEntry<T>(data, { key })
}
